import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import useRequest from "../../../hook/use-req";
import { SingleAudienceResponse } from "../../../../views/marketing-pages/campaign/types";
import { CampaignData } from "../../../types";

interface ManualTabProps {
  channel: string;
  isEmailCampaign: boolean;
  onManualSuccess: (audienceId: string, validRecords: number) => void;
  audienceById: SingleAudienceResponse | null;
  campaignData: CampaignData;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onValidation: (isValid: boolean) => void;
  onConsentChange?: (isChecked: boolean) => void;
}

const ManualTab: React.FC<ManualTabProps> = ({
  channel,
  isEmailCampaign,
  onManualSuccess,
  audienceById,
  onValidation,
  onConsentChange,
}) => {
  const token = localStorage.getItem("authToken");

  const getUserDetails = () => {
    try {
      const userDetails =
        localStorage.getItem("userDetails") ||
        localStorage.getItem("user") ||
        localStorage.getItem("user_data");
      return userDetails ? JSON.parse(userDetails) : null;
    } catch {
      return null;
    }
  };

  const getUserCountryCode = () => {
    try {
      const user = getUserDetails();
      if (user) {
        if (user.country_code) {
          let code = user.country_code;
          if (typeof code === "string") code = parseInt(code.replace("+", ""));
          return `+${code}`;
        }
        const phone = user.phone || user.phone_number;
        if (phone) {
          if (phone.includes("+234")) return "+234";
          if (phone.includes("+91")) return "+91";
          if (phone.includes("+1")) return "+1";
          if (phone.includes("+44")) return "+44";
        }
        if (user.country_name) {
          const map: Record<string, string> = {
            India: "+91", Nigeria: "+234", "United States": "+1", USA: "+1",
            "United Kingdom": "+44", UK: "+44", Canada: "+1", Australia: "+61",
            Germany: "+49", France: "+33", Spain: "+34", Italy: "+39",
            Brazil: "+55", Mexico: "+52", Japan: "+81", China: "+86",
            "South Africa": "+27",
          };
          if (map[user.country_name]) return map[user.country_name];
        }
      }
      return "+234";
    } catch {
      return "+234";
    }
  };


  const userCountryCode = getUserCountryCode();

  // --- State ---
  const [chips, setChips] = useState<
    { value: string; valid?: boolean; type?: "valid" | "invalid" | "duplicate" }[]
  >([]);
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const [dupCount, setDupCount] = useState(0);
  const [inputVal, setInputVal] = useState("");
  const [isUploadingManual, setIsUploadingManual] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [uploadedAudienceId, setUploadedAudienceId] = useState<string | null>(null);
  const isMounted = useRef(true);
  const [uploadStats, setUploadStats] = useState<{
    total: number; valid: number; duplicates: number; invalid: number; unsubscribed: number;
  } | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const uploadInProgress = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { makeRequest: uploadManualAudience } = useRequest("/audience/upload/list", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const STORAGE_KEY = `manual_audience_step_${channel}_${isEmailCampaign ? "email" : "sms"}`;

  useEffect(() => {
    resetAll();
    return () => {
      isMounted.current = false;
    };
  }, [channel, isEmailCampaign]);

  // ── Restore from sessionStorage on mount ─────────────────────────────────
useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.chips?.length > 0) {
          setChips(parsed.chips);
          setSeen(new Set(parsed.seen || []));
          setDupCount(parsed.dupCount || 0);
          setHasUploaded(parsed.hasUploaded || false);
          setUploadedAudienceId(parsed.uploadedAudienceId || null);
          setUploadStats(parsed.uploadStats || null);
        }
      } catch (e) {
        console.error("Failed to restore manual audience", e);
      }
    }
  }, [STORAGE_KEY]);


  // ── Auto-save to sessionStorage on every relevant state change ────────────
  useEffect(() => {
    if (chips.length > 0 || hasUploaded) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        chips,
        seen: Array.from(seen),
        dupCount,
        hasUploaded,
        uploadedAudienceId,
        uploadStats,
        consentChecked,
      }));
    }
  }, [chips, seen, dupCount, hasUploaded, uploadedAudienceId, uploadStats, consentChecked]);



  // ── Validation ────────────────────────────────────────────────────────────

  const updateLiveStats = () => {
    setUploadStats({ total: 0, valid: 0, duplicates: 0, invalid: 0, unsubscribed: 0 });
  };

  // ── Add Chips ─────────────────────────────────────────────────────────────
  const addChips = (values: string[]) => {
    const newChips: { value: string; valid?: boolean; type?: "valid" | "invalid" | "duplicate" }[] = [];
    let newDups = 0;
    const newSeen = new Set(seen);

    for (const raw of values) {
      const v = raw.trim();
      if (!v) continue;
      if (chips.length + newChips.length >= 10000) break;

      const normalizedV = isEmailCampaign ? v.toLowerCase() : v;

      if (newSeen.has(normalizedV)) {
        newDups++;
        newChips.push({ value: v, type: "duplicate" });
        continue;
      }
      newSeen.add(normalizedV);
      newChips.push({ value: v });
    }

    if (newChips.length > 0 || newDups > 0) {
      setChips(prev => [...prev, ...newChips]);
      setSeen(newSeen);
      setDupCount(prev => prev + newDups);

      if (newChips.length > 0 && hasUploaded) {
        setHasUploaded(false);
        setUploadedAudienceId(null);
        setUploadStats(null);
        localStorage.removeItem("audienceId");
      }
    }
  };

  const removeChip = (index: number) => {
    const removed = chips[index];
    setChips(prev => prev.filter((_, i) => i !== index));
    setSeen(prev => {
      const next = new Set(prev);
      next.delete(isEmailCampaign ? removed.value.toLowerCase() : removed.value);
      return next;
    });
  };

const resetAll = () => {
    setChips([]);
    setSeen(new Set());
    setDupCount(0);
    setInputVal("");
    setHasUploaded(false);
    setUploadedAudienceId(null);
    setUploadStats(null);
    setConsentChecked(false);
    sessionStorage.removeItem(STORAGE_KEY);
    onValidation(false);
  };
  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      addChips(text.split(/[\n,;]+/));
    } catch {
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", ",", " "].includes(e.key)) {
      e.preventDefault();
      const v = inputVal.trim();
      if (v) { addChips([v]); setInputVal(""); }
    }
    if (e.key === "Backspace" && !inputVal && chips.length > 0) {
      removeChip(chips.length - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    addChips(e.clipboardData.getData("text").split(/[\n,;]+/));
  };

  const normalizeContact = (contact: string, isEmail: boolean): string => {
    if (isEmail) {
      return contact.replace(/^__email__/, '').toLowerCase().trim();
    }

    // Phone normalization — strip whitespace/formatting only. Whatever the
    // user typed (with or without a country code) is sent to the payload
    // as-is; no country code is ever auto-prepended.
    return contact.trim().replace(/[\s\-\(\)]/g, "");
  };

  // Update formatPhoneNumbers to be consistent
  const formatPhoneNumbers = (numbers: string[]): string[] => {
    return numbers.map((n) => normalizeContact(n, false));
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleUploadContacts = async () => {
    if (chips.length === 0) {
      toast.error(`Please enter ${isEmailCampaign ? "email addresses" : "phone numbers"}`);
      return;
    }
    if (uploadInProgress.current || isUploadingManual) return;

    uploadInProgress.current = true;
    setIsUploadingManual(true);

    try {
      let contactsToSend = chips.map((c) => c.value);
      if (!isEmailCampaign) {
        contactsToSend = formatPhoneNumbers(contactsToSend);
      }

      const [response] = await uploadManualAudience({ contacts: contactsToSend });

      if (response && (response.audience_id || response.id)) {
        const audienceId = response.audience_id || response.id;
        const metadata = response?.metadata || {};

        // Create normalized sets from backend response
        const validSet = new Set(
          (response.valid_contacts || []).map((c: string) => normalizeContact(c, isEmailCampaign))
        );
        const invalidSet = new Set(
          (response.invalid_contacts || []).map((c: string) => normalizeContact(c, isEmailCampaign))
        );
        const duplicateSet = new Set(
          (response.duplicate_contacts || []).map((c: string) => normalizeContact(c, isEmailCampaign))
        );

        // Update ALL chips with correct status
        setChips((prev) =>
          prev.map((chip) => {
            const normalizedChip = normalizeContact(chip.value, isEmailCampaign);

            if (invalidSet.has(normalizedChip)) {
              return { ...chip, type: "invalid" };
            }
            if (duplicateSet.has(normalizedChip)) {
              return { ...chip, type: "duplicate" };
            }
            if (validSet.has(normalizedChip)) {
              return { ...chip, type: "valid" };
            }
            return { ...chip, type: "valid" }; // Default to valid if not in any set
          })
        );

        const stats = {
          total: metadata.total_count || chips.length,
          valid: metadata.unique_count || 0,
          duplicates: metadata.duplicates_count || 0,
          invalid: metadata.invalids_count || 0,
          unsubscribed: metadata.unsubscribed_count || 0,
        };

        setUploadStats(stats);
        setUploadedAudienceId(audienceId);
        setHasUploaded(true);

        toast.success(`Success! ${stats.valid} valid contacts processed`);

        onManualSuccess(audienceId, stats.valid);
        onValidation(stats.valid > 0);
      } else {
        toast.error(response?.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload contacts");
    } finally {
      setIsUploadingManual(false);
      uploadInProgress.current = false;
    }
  };
  // Live stats effect
  useEffect(() => {
    if (!hasUploaded) updateLiveStats();
  }, [chips, dupCount, hasUploaded]);

  // Load existing audience from props
  useEffect(() => {
    if (!isInitialized && audienceById?.type === "manual" && audienceById?.contacts?.length > 0) {
      setIsInitialized(true);
      const existing = audienceById.contacts.map((v: string) => ({ value: v }));
      setChips(existing);
      setSeen(new Set(audienceById.contacts));
      setUploadedAudienceId(audienceById.audience_id);
      setHasUploaded(true);
      setUploadStats({
        total: audienceById.total_count || 0,
        valid: audienceById.unique_count || 0,
        duplicates: audienceById.duplicates_count || 0,
        invalid: audienceById.invalids_count || 0,
        unsubscribed: audienceById.unsubscribed_count || 0,
      });
      onManualSuccess(audienceById.audience_id, audienceById.contacts.length);
      onValidation(true);
    }
  }, [audienceById, isInitialized]);

  const placeholder = isEmailCampaign
    ? "Type or paste email addresses e.g. user@example.com"
    : `Type or paste the phone numbers you intend to reach e.g. ${userCountryCode}9900990099`;

  useEffect(() => {
    onConsentChange?.(consentChecked);
  }, [consentChecked, onConsentChange]);

  return (
    <div className="space-y-3">
      {/* Main Chip Input Area */}
      <div
        onClick={() => inputRef.current?.focus()}
        className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-2xl cursor-text"
      >
        <div className="flex flex-wrap gap-1.5 p-3 pb-2">
          {chips.map((chip, i) => (
            <span
              key={`${chip.value}-${i}`}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium whitespace-nowrap border
                ${chip.type === "invalid"
                  ? "bg-red-50 dark:bg-red-500/15 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400"
                  : chip.type === "duplicate"
                    ? "bg-blue-50 dark:bg-blue-500/15 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400"
                    : chip.type === "valid"
                      ? "bg-green-50 dark:bg-green-500/15 border-green-300 dark:border-green-700 text-green-700 dark:text-green-400"
                      : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200"
                }`}
            >
              {chip.value}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeChip(i); }}
                className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 leading-none text-sm font-medium"
              >
                ×
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={chips.length === 0 ? placeholder : ""}
            className="border-none outline-none text-sm text-gray-800 dark:text-gray-100 bg-transparent flex-1 min-w-[200px] py-1 placeholder-gray-400 dark:placeholder-gray-500"
            style={{ fontFamily: "inherit" }}
          />
        </div>

        {/* Footer bar */}
        <div className="flex items-center justify-between border-gray-100 dark:border-gray-700 px-3 py-2.5 flex-wrap gap-2">
          <div className="flex items-center gap-0 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
            <span>
              Total <strong className="text-gray-700 dark:text-gray-300">
                {hasUploaded ? uploadStats?.total : chips.length}
              </strong> / 1,000 {isEmailCampaign ? "Emails" : "Numbers"}
            </span>
            <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
              Valid <strong className="text-green-600 dark:text-green-400 ml-0.5">{uploadStats?.valid || 0}</strong>
            </span>
            <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
              Invalid <strong className="text-red-600 dark:text-red-400 ml-0.5">{uploadStats?.invalid || 0}</strong>
            </span>
            <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
              Duplicates <strong className="text-blue-600 dark:text-blue-400 ml-0.5">{uploadStats?.duplicates || 0}</strong>
            </span>
            {isEmailCampaign && (
              <>
                <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />
                  Unsubscribed <strong className="text-orange-600 dark:text-orange-400 ml-0.5">{uploadStats?.unsubscribed || 0}</strong>
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              title="Reset"
              onClick={resetAll}
              className="w-8 h-8 flex items-center justify-center border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>

            <button
              type="button"
              title="Paste from clipboard"
              onClick={pasteFromClipboard}
              className="w-8 h-8 flex items-center justify-center border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              </svg>
            </button>

            <button
              type="button"
              onClick={handleUploadContacts}
              disabled={chips.length === 0 || isUploadingManual}
              className="h-8 px-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 text-xs font-semibold text-gray-800 dark:text-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isUploadingManual ? "Uploading..." : hasUploaded ? "Re-validate Contacts" : "Validate Contacts"}
            </button>
          </div>
        </div>
      </div>

      {/* Consent Checkbox */}
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={consentChecked}
          onChange={(e) => setConsentChecked(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#86EFAC] rounded border-black dark:border-gray-500 border cursor-pointer"
        />
        {channel === "email" ? (
          <span className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed">
            Ensure all recipients have opted in to receive promotional emails from your business, and that all email addresses have been validated before proceeding.
          </span>
        ) : (
          <span className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed">
            Please ensure all contacts have consented to receive promotional messages. Include country codes only for numbers outside your default region.
          </span>
        )}
      </label>
    </div>
  );
};

export default ManualTab;